import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '@/stores/authStore';
import type { AgentStats, ClaimResult } from '@/lib/bots/edge';
import { claimBot, releaseBot } from '@/lib/bots/edge';
import { deriveMotion, frontOf, HIST, type Motion, type Sample } from '@/lib/bots/telemetry';

export type FleetStatus = 'loading' | 'ok' | 'not-configured' | 'error';

/**
 * Live edge-fleet poll for the Position Tracker bot zones: the agent list +
 * per-agent climb/ETA motion (from a rolling queue-history ref) + the control
 * actions (pause/resume/kill/claim/release), each refreshing on success. The
 * relay scopes mutating control to managers/admins or the agent's claimer, so a
 * trader's pause/kill on a bot they don't own simply 403s.
 */
export function useEdgeFleet(opts?: { enabled?: boolean; pollMs?: number }): {
  agents: AgentStats[];
  motion: Map<string, Motion>;
  status: FleetStatus;
  dryRun: boolean | null;
  error: string | null;
  refresh: () => void;
  pause: (id: string) => Promise<boolean>;
  resume: (id: string) => Promise<boolean>;
  kill: (id: string) => Promise<boolean>;
  claim: (id: string, ticker?: string) => Promise<ClaimResult>;
  release: (id: string) => Promise<boolean>;
} {
  const enabled = opts?.enabled ?? true;
  const pollMs = opts?.pollMs ?? 2000;
  const [agents, setAgents] = useState<AgentStats[]>([]);
  const [status, setStatus] = useState<FleetStatus>('loading');
  const [dryRun, setDryRun] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const histRef = useRef<Map<string, Sample[]>>(new Map());

  const ingest = useCallback((list: AgentStats[]) => {
    const now = Date.now();
    const m = histRef.current;
    const liveIds = new Set(list.map((a) => a.id));
    for (const id of [...m.keys()]) if (!liveIds.has(id)) m.delete(id);
    for (const a of list) {
      const arr = m.get(a.id) ?? [];
      arr.push({ ms: now, front: frontOf(a) });
      if (arr.length > HIST) arr.splice(0, arr.length - HIST);
      m.set(a.id, arr);
    }
    setAgents(list);
    setStatus('ok');
    setError(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await apiFetch('/api/edge/agents');
      if (res.status === 503) {
        const b = await res.json().catch(() => ({}));
        setStatus('not-configured');
        setError(b.error ?? 'edge not configured');
        setDryRun(null);
        return;
      }
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        setStatus('error');
        setError(b.error ?? `HTTP ${res.status}`);
        return;
      }
      const data = await res.json();
      setDryRun(typeof data.dryRun === 'boolean' ? data.dryRun : null);
      ingest(Array.isArray(data.agents) ? data.agents : []);
    } catch (e) {
      setStatus('error');
      setError((e as Error).message);
    }
  }, [ingest]);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
    const t = setInterval(() => void refresh(), pollMs);
    return () => clearInterval(t);
  }, [enabled, pollMs, refresh]);

  const motion = useMemo(() => {
    const out = new Map<string, Motion>();
    for (const a of agents) out.set(a.id, deriveMotion(histRef.current.get(a.id)));
    return out;
  }, [agents]);

  const post = useCallback(
    async (path: string, method: 'POST' | 'DELETE'): Promise<boolean> => {
      try {
        const res = await apiFetch(path, { method });
        if (res.ok) void refresh();
        return res.ok;
      } catch {
        return false;
      }
    },
    [refresh],
  );

  const claim = useCallback(
    async (id: string, ticker?: string) => {
      const r = await claimBot(id, ticker);
      // ALWAYS refresh — even a lost claim (409) must update the list so the
      // real owner shows, instead of a stale Claim click silently no-op'ing.
      void refresh();
      return r;
    },
    [refresh],
  );
  const release = useCallback(
    async (id: string) => {
      const ok = await releaseBot(id);
      if (ok) void refresh();
      return ok;
    },
    [refresh],
  );

  return {
    agents,
    motion,
    status,
    dryRun,
    error,
    refresh,
    pause: (id) => post(`/api/edge/agents/${encodeURIComponent(id)}/pause`, 'POST'),
    resume: (id) => post(`/api/edge/agents/${encodeURIComponent(id)}/resume`, 'POST'),
    kill: (id) => post(`/api/edge/agents/${encodeURIComponent(id)}`, 'DELETE'),
    claim,
    release,
  };
}
